import React, { useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  X,
  LayoutTemplate,
  Globe,
  LayoutDashboard,
  Palette,
  CalendarClock,
  FileText,
  Layers,
} from "lucide-react";
import { useScheduleCall } from "../context/ScheduleCallContext";
import { bwsShowcaseFeatures } from "../config/businessWebsiteSuite";

const featureIcons = {
  "public-website": Globe,
  "online-booking": CalendarClock,
  "instant-quotes": FileText,
  "owner-dashboard": LayoutDashboard,
  branding: Palette,
  "industry-templates": Layers,
};

const BusinessWebsiteSuiteShowcase = ({ isOpen, onClose }) => {
  const reduceMotion = useReducedMotion();
  const { openScheduleCall } = useScheduleCall();

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handleDiscussOnboarding = () => {
    onClose();
    setTimeout(openScheduleCall, 200);
  };

  const overlayVariants = reduceMotion
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
    : { hidden: { opacity: 0 }, visible: { opacity: 1 } };

  const modalVariants = reduceMotion
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.96, y: 24 },
        visible: { opacity: 1, scale: 1, y: 0 },
      };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          transition={{ duration: reduceMotion ? 0 : 0.2 }}
        >
          <motion.button
            type="button"
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            onClick={onClose}
            aria-label="Close Business Website Suite showcase"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="bws-showcase-title"
            className="relative w-full sm:max-w-4xl lg:max-w-5xl max-h-[94vh] sm:max-h-[92vh] overflow-y-auto glass-panel border-white/10 rounded-t-2xl sm:rounded-2xl shadow-2xl shadow-black/50"
            variants={modalVariants}
            transition={{ duration: reduceMotion ? 0 : 0.28, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 p-6 pb-4 bg-dark-400/95 backdrop-blur-xl border-b border-white/5 rounded-t-2xl">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent-cyan/30 bg-accent-cyan/10 text-accent-cyan text-xs font-medium mb-3">
                  <LayoutTemplate
                    className="w-3.5 h-3.5"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                  Product Showcase
                </div>
                <h2
                  id="bws-showcase-title"
                  className="font-display text-xl sm:text-2xl font-bold text-white"
                >
                  Business Website Suite
                </h2>
                <p className="text-sm text-gray-400 mt-1 max-w-lg">
                  All-in-one website, booking, and CRM for service businesses —
                  screenshots from the live demo platform.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>

            <div className="p-6 pt-2 space-y-10">
              {bwsShowcaseFeatures.map((feature, index) => {
                const Icon = featureIcons[feature.id] || Globe;
                const reversed = index % 2 === 1;

                return (
                  <motion.article
                    key={feature.id}
                    className={`flex flex-col gap-5 ${
                      reversed ? "lg:flex-row-reverse" : "lg:flex-row"
                    } lg:items-center`}
                    initial={
                      reduceMotion ? {} : { opacity: 0, y: 20 }
                    }
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: reduceMotion ? 0 : 0.05 + index * 0.06,
                      duration: 0.4,
                    }}
                  >
                    <div className="lg:w-[52%] flex-shrink-0">
                      <div className="rounded-xl overflow-hidden border border-white/10 bg-dark-200/50 shadow-3d group">
                        <img
                          src={feature.image}
                          alt={`${feature.title} screenshot`}
                          className="w-full h-auto object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
                          loading="lazy"
                        />
                      </div>
                    </div>

                    <div className="lg:flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-accent-cyan/10 border border-accent-cyan/20 flex items-center justify-center text-accent-cyan flex-shrink-0">
                          <Icon className="w-5 h-5" strokeWidth={1.75} />
                        </div>
                        <h3 className="font-display text-lg font-bold text-white">
                          {feature.title}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-400 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </motion.article>
                );
              })}

              <div className="pt-4 border-t border-white/5">
                <div className="glass-panel p-6 md:p-8 text-center bg-gradient-to-br from-accent-cyan/10 to-primary-600/5">
                  <h3 className="font-display text-lg font-bold text-white mb-2">
                    Ready to see it in action?
                  </h3>
                  <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
                    Book a walkthrough and we'll show you booking flows, the
                    owner dashboard, and how it fits your business.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <motion.button
                      type="button"
                      onClick={handleDiscussOnboarding}
                      className="px-6 py-3 rounded-xl text-sm font-semibold text-gray-300 border border-white/10 hover:border-primary-500/40 hover:text-white hover:bg-white/5 transition-colors"
                      whileHover={reduceMotion ? {} : { scale: 1.02 }}
                      whileTap={reduceMotion ? {} : { scale: 0.98 }}
                    >
                      Discuss Product Onboarding
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BusinessWebsiteSuiteShowcase;
